import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  IconPhoto,
  IconUpload,
  IconTrash,
  IconCheck,
  IconRefresh,
  IconTrashX,
  IconSelectAll,
  IconX,
} from "@tabler/icons-react";
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Badge,
  ProgressBar,
  Spinner,
  Form,
} from "react-bootstrap";
import {
  listImages,
  uploadImage,
  bulkUploadImages,
  deleteImage,
  createImageFormData,
  createBulkImageFormData,
} from "../../../services/AgentsServices";
import Swal from "sweetalert2";
import { Hash } from "../../../helpers/Helpers";

const ImageLibrary = ({ hubMode = false, agentId = null, agentContext = null }) => {
  const [apiImageLibrary, setApiImageLibrary] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [accountLimits, setAccountLimits] = useState({
    current_count: 0,
    max_allowed: 10,
    remaining_slots: 10,
    usage_percentage: 0
  });
  const fileInputRef = useRef(null);

  // Configurações
  const maxFileSize = 3 * 1024 * 1024; // 3MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  // Função para validar arquivo de imagem
  const validateImageFile = useCallback((file) => {
    const errors = [];
    
    if (file.size > maxFileSize) {
      errors.push('Arquivo muito grande (máximo 3MB)');
    }
    
    if (!allowedTypes.includes(file.type)) {
      errors.push('Tipo de arquivo não suportado (apenas JPG, PNG, GIF, WEBP)');
    }
    
    return errors;
  }, []);

  // Função para formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Função para mostrar erros detalhados baseados nos códigos da API
  const showDetailedError = (error, context = 'operação') => {
    console.error(`Erro na ${context}:`, error);
    
    let title = "Erro";
    let message = "Ocorreu um erro inesperado. Tente novamente.";
    let icon = "error";
    let html = null;

    if (error.response) {
      const { status, data } = error.response;
      const errorCode = data.error_code;
      
      // Tratamento baseado nos códigos específicos da API
      switch (errorCode) {
        case 'FILE_TOO_LARGE':
          title = "Arquivo Muito Grande";
          const details = data.details || {};
          message = `O arquivo "${details.file_name || 'selecionado'}" possui ${details.file_size_mb || 'um tamanho'}MB. O limite é de ${details.max_size_mb || 1}MB por imagem.`;
          break;
          
        case 'MAX_IMAGES_EXCEEDED':
          title = "Limite de Imagens Atingido";
          const limitDetails = data.details || {};
          html = `
            <div style="text-align: left;">
              <p>Você já possui <strong>${limitDetails.current_count || 10} imagens</strong> no limite máximo de <strong>${limitDetails.max_allowed || 10}</strong>.</p>
              <p style="margin-top: 15px;"><strong>💡 Sugestão:</strong> ${limitDetails.suggestion || 'Remova algumas imagens antes de adicionar novas'}</p>
            </div>
          `;
          break;
          
        case 'INVALID_IMAGE_FORMAT':
          title = "Arquivo Inválido";
          const formatDetails = data.details || {};
          message = `O arquivo "${formatDetails.file_name || 'selecionado'}" não é uma imagem válida. Use apenas JPG, PNG, GIF, BMP ou WEBP.`;
          break;
          
        default:
          // Fallback para status codes
          switch (status) {
            case 400:
              title = "Dados Inválidos";
              message = data.error || data.message || "Os dados enviados não são válidos.";
              break;
            case 413:
              title = "Arquivo Muito Grande";
              message = "Um ou mais arquivos excedem o tamanho máximo permitido (3MB).";
              break;
            case 415:
              title = "Formato Não Suportado";
              message = "Um ou mais arquivos possuem formato não aceito.";
              break;
            default:
              title = `Erro ${status}`;
              message = data.error || data.message || "Erro desconhecido no servidor.";
          }
      }
    } else if (error.request) {
      title = "Erro de Conexão";
      message = "Não foi possível conectar ao servidor. Verifique sua conexão de internet.";
    } else {
      title = "Erro Local";
      message = error.message || "Erro desconhecido na aplicação.";
    }

    Swal.fire({
      icon,
      title,
      ...(html ? { html } : { text: message }),
      confirmButtonText: "Entendi",
      customClass: {
        popup: 'swal-wide'
      }
    });
  };

  // Carregar imagens da biblioteca
  const loadImageLibrary = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = agentId ? { agent_id: agentId } : {};
      const response = await listImages(params);
      const data = response.data;
      
      // Atualizar lista de imagens
      setApiImageLibrary(data.results || data || []);
      
      // Atualizar informações de limite se disponíveis
      if (data.count !== undefined) {
        setAccountLimits(prev => ({
          ...prev,
          current_count: data.count,
          remaining_slots: Math.max(0, 10 - data.count),
          usage_percentage: Math.round((data.count / 10) * 100)
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar biblioteca de imagens:', error);
      showDetailedError(error, 'carregamento de imagens');
      setApiImageLibrary([]);
    } finally {
      setIsLoading(false);
    }
  }, [agentId]);

  // Handler para upload de imagens
  const handleImageUpload = useCallback(async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    setIsUploading(true);
    setImageLoading(true);
    setUploadProgress(0);

    const validFiles = [];
    const invalidFiles = [];

    // Validar todos os arquivos
    files.forEach(file => {
      const errors = validateImageFile(file);
      if (errors.length === 0) {
        validFiles.push(file);
      } else {
        invalidFiles.push({ file, errors });
      }
    });

    // Mostrar erros de arquivos inválidos
    if (invalidFiles.length > 0) {
      const errorList = invalidFiles.map(({ file, errors }) => 
        `• ${file.name}: ${errors.join(', ')}`
      ).join('\n');
      
      Swal.fire({
        icon: "warning",
        title: "Arquivos com Problemas",
        html: `<div style="text-align: left;">Os seguintes arquivos não puderam ser processados:<br><br><code style="white-space: pre-line;">${errorList}</code></div>`,
        confirmButtonText: "Entendi"
      });
    }

    if (validFiles.length === 0) {
      setIsUploading(false);
      setImageLoading(false);
      return;
    }

    try {
      if (validFiles.length === 1) {
        // Upload único
        const file = validFiles[0];
        setUploadProgress(30);

        const formData = createImageFormData(file, agentId ? { agent_id: agentId } : {});
        
        setUploadProgress(50);
        const response = await uploadImage(formData);
        setUploadProgress(100);

        // Atualizar informações de limite com resposta
        const responseData = response.data;
        if (responseData.account_images_count !== undefined) {
          setAccountLimits(prev => ({
            ...prev,
            current_count: responseData.account_images_count,
            remaining_slots: responseData.remaining_slots || Math.max(0, 10 - responseData.account_images_count),
            usage_percentage: Math.round((responseData.account_images_count / 10) * 100)
          }));
        }

        Swal.fire({
          icon: "success",
          title: "Imagem Carregada!",
          html: `
            <div style="text-align: left;">
              <p><strong>"${responseData.name || file.name}"</strong> foi adicionada com sucesso!</p>
              ${responseData.remaining_slots !== undefined ? 
                `<p style="margin-top: 10px; color: #666;">
                  📊 Você possui <strong>${responseData.account_images_count || 1} imagens</strong> de 10 permitidas<br>
                  <strong>${responseData.remaining_slots || 9} </strong> restantes
                </p>` : ''
              }
            </div>
          `,
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        // Upload múltiplo
        setUploadProgress(20);
        
        const formData = createBulkImageFormData(validFiles, agentId ? { agent_id: agentId } : {});
        
        // Simular progresso gradual
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 15, 85));
        }, 300);

        const response = await bulkUploadImages(formData);
        
        clearInterval(progressInterval);
        setUploadProgress(100);

        const responseData = response.data;
        const { uploaded = [], errors: uploadErrors = [], total_uploaded = 0, total_errors = 0, account_images_count, remaining_slots } = responseData;

        // Atualizar informações de limite
        if (account_images_count !== undefined) {
          setAccountLimits(prev => ({
            ...prev,
            current_count: account_images_count,
            remaining_slots: remaining_slots || Math.max(0, 10 - account_images_count),
            usage_percentage: Math.round((account_images_count / 10) * 100)
          }));
        }

        // Mostrar resultado do upload múltiplo com detalhes
        if (total_errors > 0) {
          const errorsList = uploadErrors.map(err => `• ${err.file}: ${err.error}`).join('<br>');
          Swal.fire({
            icon: "warning",
            title: "Upload Parcialmente Concluído",
            html: `
              <div style="text-align: left;">
                <p><strong>✅ ${total_uploaded} imagens</strong> enviadas com sucesso</p>
                <p><strong>❌ ${total_errors} imagens</strong> com erro:</p>
                <div style="background: #f8f9fa; padding: 10px; border-radius: 5px; margin: 10px 0; font-size: 0.9em;">
                  ${errorsList}
                </div>
                ${remaining_slots !== undefined ? 
                  `<p style="margin-top: 15px; color: #666;">
                    📊 Total de imagens: <strong>${account_images_count}</strong> / 10<br>
                    Vagas: <strong>${remaining_slots}</strong>
                  </p>` : ''
                }
              </div>
            `,
          });
        } else {
          Swal.fire({
            icon: "success",
            title: `${total_uploaded} Imagens Carregadas!`,
            html: `
              <div style="text-align: left;">
                <p>Todas as <strong>${total_uploaded} imagens</strong> foram enviadas com sucesso!</p>
                ${remaining_slots !== undefined ? 
                  `<p style="margin-top: 15px; color: #666;">
                    📊 Total de imagens: <strong>${account_images_count}</strong> / 10<br>
                    Vagas: <strong>${remaining_slots}</strong>
                  </p>` : ''
                }
              </div>
            `,
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }

      // Recarregar lista de imagens após upload
      await loadImageLibrary();

    } catch (error) {
      showDetailedError(error, 'upload');
    } finally {
      setIsUploading(false);
      setImageLoading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [validateImageFile, loadImageLibrary, agentId]);

  // Handler para remover imagem
  const handleRemoveImage = useCallback(async (imageId) => {
    const result = await Swal.fire({
      title: "Remover imagem?",
      text: "Esta ação não poderá ser desfeita. A imagem será removida da biblioteca.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await deleteImage(imageId);
      
      // Atualizar lista local
      const removedImage = apiImageLibrary.find(img => img.id === imageId);
      setApiImageLibrary(prev => prev.filter(img => img.id !== imageId));
      
      // Atualizar informações de limite
      setAccountLimits(prev => ({
        ...prev,
        current_count: Math.max(0, prev.current_count - 1),
        remaining_slots: Math.min(10, prev.remaining_slots + 1),
        usage_percentage: Math.round(((prev.current_count - 1) / 10) * 100)
      }));

      const responseData = response.data;
      Swal.fire({
        icon: "success",
        title: "Imagem Removida!",
        html: `
          <div style="text-align: left;">
            <p><strong>"${responseData.image_name || removedImage?.name || 'Imagem'}"</strong> foi removida com sucesso!</p>
            <p style="margin-top: 10px; color: #666;">
              📊 Agora você possui <strong>${accountLimits.current_count - 1} imagens</strong> de 10<br>
              <strong>${accountLimits.remaining_slots + 1} vagas</strong> disponíveis
            </p>
          </div>
        `,
        timer: 3000,
        showConfirmButton: false,
      });

    } catch (error) {
      showDetailedError(error, 'remoção');
    }
  }, [accountLimits.current_count, accountLimits.remaining_slots, apiImageLibrary]);

  // Handler para refresh manual
  const handleRefresh = useCallback(async () => {
    await loadImageLibrary();
  }, [loadImageLibrary]);

  // Funções para seleção múltipla
  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedImages([]);
  }, [isSelectionMode]);

  const toggleImageSelection = useCallback((image) => {
    setSelectedImages(prev => {
      const isSelected = prev.some(img => img.id === image.id);
      if (isSelected) {
        return prev.filter(img => img.id !== image.id);
      } else {
        return [...prev, image];
      }
    });
  }, []);

  const selectAllImages = useCallback(() => {
    if (selectedImages.length === apiImageLibrary.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages([...apiImageLibrary]);
    }
  }, [selectedImages.length, apiImageLibrary]);

  // Handler para deletar múltiplas imagens
  const handleBulkDelete = useCallback(async () => {
    if (selectedImages.length === 0) return;

    const result = await Swal.fire({
      title: `Remover ${selectedImages.length} imagem(ns)?`,
      text: "Esta ação não poderá ser desfeita. As imagens serão removidas da biblioteca.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      setIsDeletingBulk(true);
      
      // Deletar uma por uma (pode ser otimizado com bulk delete se a API suportar)
      for (const image of selectedImages) {
        await deleteImage(image.id);
      }
      
      // Atualizar lista local removendo as imagens deletadas
      const deletedIds = selectedImages.map(img => img.id);
      setApiImageLibrary(prev => prev.filter(img => !deletedIds.includes(img.id)));
      
      // Limpar seleção
      setSelectedImages([]);
      setIsSelectionMode(false);

      Swal.fire({
        icon: "success",
        title: "Imagens removidas!",
        text: `${selectedImages.length} imagem(ns) removida(s) com sucesso.`,
        timer: 3000,
        showConfirmButton: false,
      });

    } catch (error) {
      console.error('Erro no bulk delete:', error);
      showDetailedError(error, 'remoção em lote');
    } finally {
      setIsDeletingBulk(false);
    }
  }, [selectedImages]);

  // Carregar imagens quando o componente montar
  useEffect(() => {
    loadImageLibrary();
  }, [loadImageLibrary]);

  // Usar apenas as imagens da API (evita duplicação)
  const allImages = useMemo(() => {
    return apiImageLibrary;
  }, [apiImageLibrary]);

  // Renderização condicional do header baseado no hubMode
  const renderHeader = () => {
    if (hubMode) {
      return (
        <div className="mb-4">
          {/* Header principal */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-3">
            <div className="d-flex align-items-center">
              <IconPhoto className="me-3 text-primary" size={24} />
              <div>
                <h4 className="mb-0">Biblioteca de Imagens</h4>
                {agentContext && (
                  <p className="text-muted mb-0 small">
                    Imagens para o agente <strong>{agentContext.agent_name || agentContext.name}</strong>
                  </p>
                )}
              </div>
            </div>
            
            {/* Ações mobile-first */}
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Badge bg="info" className="px-2 py-1">
                {allImages.length} imagens
              </Badge>
              <Badge bg={accountLimits.usage_percentage > 90 ? "danger" : accountLimits.usage_percentage > 75 ? "warning" : "success"} className="px-2 py-1">
                {accountLimits.remaining_slots} restantes
              </Badge>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
                className="d-flex align-items-center"
              >
                <IconRefresh size={16} className={isLoading ? "spinner-border" : ""} />
                <span className="d-none d-sm-inline ms-1">Atualizar</span>
              </Button>
            </div>
          </div>
          
          {/* Barra de ações para seleção múltipla */}
          {allImages.length > 0 && (
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Button 
                variant={isSelectionMode ? "warning" : "outline-secondary"} 
                size="sm"
                onClick={toggleSelectionMode}
                className="d-flex align-items-center"
              >
                <IconSelectAll size={16} className="me-1" />
                {isSelectionMode ? "Cancelar" : "Selecionar"}
              </Button>
              {isSelectionMode && selectedImages.length > 0 && (
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={isDeletingBulk}
                  className="d-flex align-items-center"
                >
                  <IconTrashX size={16} className="me-1" />
                  {isDeletingBulk ? "Removendo..." : `Remover ${selectedImages.length}`}
                </Button>
              )}
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="mb-4">
        {/* Header principal */}
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-3 gap-3">
          <div className="d-flex align-items-center">
            <IconPhoto className="me-3 text-primary d-none d-sm-block" size={32} />
            <IconPhoto className="me-2 text-primary d-sm-none" size={24} />
            <div>
              <h2 className="mb-0 h4 h-lg-2">Biblioteca de Imagens</h2>
              <p className="text-muted mb-0 small">Gerencie as imagens para uso nos seus agentes IA</p>
            </div>
          </div>
          
          {/* Stats e refresh */}
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Badge bg="info" className="px-2 py-1">
              {allImages.length} imagens
            </Badge>
            <Badge bg={accountLimits.usage_percentage > 90 ? "danger" : accountLimits.usage_percentage > 75 ? "warning" : "success"} className="px-2 py-1">
              {accountLimits.remaining_slots} restantes
            </Badge>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              className="d-flex align-items-center"
            >
              <IconRefresh size={16} className={isLoading ? "spinner-border" : ""} />
              <span className="d-none d-md-inline ms-1">Atualizar</span>
            </Button>
          </div>
        </div>
        
        {/* Ações de seleção múltipla */}
        {allImages.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Button 
              variant={isSelectionMode ? "warning" : "outline-secondary"} 
              size="sm"
              onClick={toggleSelectionMode}
              className="d-flex align-items-center"
            >
              <IconSelectAll size={16} className="me-1" />
              {isSelectionMode ? "Cancelar" : "Selecionar"}
            </Button>
            {isSelectionMode && selectedImages.length > 0 && (
              <Button 
                variant="danger" 
                size="sm"
                onClick={handleBulkDelete}
                disabled={isDeletingBulk}
                className="d-flex align-items-center"
              >
                <IconTrashX size={16} className="me-1" />
                {isDeletingBulk ? "Removendo..." : `Remover ${selectedImages.length}`}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={hubMode ? "p-4" : "container-fluid py-4"}>
      {renderHeader()}

      <Alert variant="info" className="mb-4">
        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3">
          <div className="flex-grow-1">
            <h6 className="mb-2">📸 Biblioteca Central de Imagens</h6>
            <p className="mb-2 small">
              Esta é sua biblioteca central de imagens para uso nos agentes IA.
              As imagens aqui ficam disponíveis para inserção nas conversas de qualquer agente.
            </p>
          </div>
        </div>
        
        {/* Barra de progresso de uso - mobile otimizado */}
        <div className="mb-3">
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 gap-1">
            <small className="fw-bold">Uso da Biblioteca:</small>
            <small className="text-muted">{accountLimits.current_count} / {accountLimits.max_allowed} imagens ({accountLimits.usage_percentage}%)</small>
          </div>
          <ProgressBar 
            now={accountLimits.usage_percentage} 
            variant={
              accountLimits.usage_percentage > 90 ? "danger" : 
              accountLimits.usage_percentage > 75 ? "warning" : "success"
            }
            style={{ height: "12px" }}
            className="rounded"
          />
        </div>
        
        {/* Lista compacta para mobile */}
        <Row className="g-2 small">
          <Col xs={12} sm={6}>
            <strong>Tipos:</strong> JPG, PNG, GIF, WEBP
          </Col>
          <Col xs={12} sm={6}>
            <strong>Máximo:</strong> 3MB por imagem
          </Col>
          <Col xs={12} sm={6}>
            <strong>Limite:</strong> {accountLimits.max_allowed} imagens
          </Col>
          <Col xs={12} sm={6}>
            <strong>Disponível:</strong> Para todos os agentes
          </Col>
        </Row>
      </Alert>

      {/* Loading state */}
      {isLoading && (
        <Card className="text-center py-5">
          <Card.Body>
            <Spinner className="mb-3" />
            <h5>Carregando imagens...</h5>
            <p className="text-muted mb-0">
              Buscando imagens da sua biblioteca
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Lista de imagens carregadas - mobile otimizado */}
      {!isLoading && allImages.length > 0 && (
        <Card>
          <Card.Header className="bg-light">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <h5 className="mb-0 h6 h-md-5">
                  📸 Imagens na Biblioteca
                </h5>
                {isSelectionMode && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={selectAllImages}
                    className="d-flex align-items-center"
                  >
                    <IconSelectAll size={14} className="me-1" />
                    <span className="d-none d-sm-inline">{selectedImages.length === allImages.length ? "Desmarcar todas" : "Selecionar todas"}</span>
                    <span className="d-sm-none">{selectedImages.length === allImages.length ? "Desmarcar" : "Todas"}</span>
                  </Button>
                )}
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {isSelectionMode && selectedImages.length > 0 && (
                  <Badge bg="warning" className="px-2 py-1">{selectedImages.length} selecionadas</Badge>
                )}
                <Badge bg="primary" className="px-2 py-1">{allImages.length} imagens</Badge>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-2 p-md-3">
            {/* Grid responsivo mobile-first */}
            <Row className="g-2 g-md-3">
              {allImages.map((image) => {
                const isSelected = selectedImages.some(img => img.id === image.id);
                
                return (
                  <Col xs={6} sm={4} md={6} lg={4} xl={3} key={image.id}>
                    <Card className={`h-100 border ${isSelectionMode && isSelected ? 'border-primary bg-light' : ''}`}>
                      {/* Container da imagem responsivo */}
                      <div
                        className="position-relative"
                        style={{
                          height: "160px",
                          overflow: "hidden",
                        }}
                      >
                        <Card.Img
                          variant="top"
                          src={image.url}
                          alt={image.name}
                          loading="lazy"
                          className="w-100 h-100"
                          style={{
                            objectFit: "cover",
                          }}
                        />
                        
                        {/* Checkbox em modo seleção */}
                        {isSelectionMode && (
                          <div className="position-absolute top-0 end-0 p-2">
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleImageSelection(image)}
                              className="fs-5"
                              style={{ 
                                transform: "scale(1.3)",
                                filter: "drop-shadow(0 0 3px rgba(0,0,0,0.5))"
                              }}
                            />
                          </div>
                        )}
                        
                        {/* Botão de remoção quando não em modo seleção */}
                        {!isSelectionMode && (
                          <div className="position-absolute top-0 end-0 p-2">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveImage(image.id)}
                              title="Remover imagem"
                              disabled={imageLoading}
                              className="rounded-circle p-1 d-flex align-items-center justify-content-center"
                              style={{
                                width: "32px",
                                height: "32px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                              }}
                            >
                              {imageLoading ? (
                                <Spinner size="sm" />
                              ) : (
                                <IconTrash size={14} />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Card body compacto */}
                      <Card.Body className="p-2">
                        <h6 className="mb-1 text-truncate small" title={image.name}>
                          {image.name}
                        </h6>
                        <div className="d-flex flex-wrap gap-1 mb-1">
                          <Badge bg="light" text="dark" className="small">
                            {formatFileSize(image.size || 0)}
                          </Badge>
                        </div>
                        <small className="text-muted d-none d-sm-block" style={{ fontSize: "0.7rem" }}>
                          {new Date(image.created_at || new Date()).toLocaleDateString('pt-BR')}
                        </small>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Área de Upload - mobile otimizada */}
      {!isLoading && (
        <Card className="my-4">
          <Card.Header>
            <h6 className="mb-0">📤 Carregar Novas Imagens</h6>
          </Card.Header>
          <Card.Body className="p-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
              disabled={isUploading}
            />
            
            <div className="text-center py-3">
              <IconUpload size={40} className="text-muted mb-3" />
              
              <h6 className="h6">Adicionar Imagens</h6>
              
              <p className="text-muted mb-3 small">
                Expanda sua biblioteca com novas imagens para os agentes
              </p>

              {/* Botão responsivo */}
              <div className="d-grid gap-2 d-sm-flex justify-content-sm-center">
                <Button
                  variant={accountLimits.remaining_slots === 0 ? "secondary" : "primary"}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || accountLimits.remaining_slots === 0}
                  className="d-flex align-items-center justify-content-center gap-2"
                  style={{ minHeight: "44px" }}
                >
                  <IconUpload size={16} />
                  <span>
                    {accountLimits.remaining_slots === 0 ? "Limite Atingido" : 
                     isUploading ? "Carregando..." : "Selecionar Imagens"}
                  </span>
                </Button>
              </div>
              
              {/* Alertas responsivos */}
              {accountLimits.remaining_slots === 0 && (
                <Alert variant="warning" className="mt-3 mb-0 small">
                  <strong>⚠️ Limite atingido!</strong><br />
                  Você atingiu o limite de {accountLimits.max_allowed} imagens. Remova algumas para adicionar novas.
                </Alert>
              )}

              {/* Informações compactas */}
              <div className="mt-3">
                <Row className="g-1 small text-muted justify-content-center">
                  <Col xs={12} sm={6} className="text-center text-sm-end">
                    Máximo 3MB por imagem
                  </Col>
                  <Col xs={12} sm={6} className="text-center text-sm-start">
                    JPG, PNG, GIF, WEBP
                  </Col>
                </Row>
              </div>
            </div>

            {/* Barra de progresso mobile otimizada */}
            {isUploading && (
              <div className="mt-3 pt-3 border-top">
                <div className="mb-2 d-flex justify-content-between align-items-center">
                  <small className="fw-bold">Enviando imagens</small>
                  <small className="text-muted">{Math.round(uploadProgress)}%</small>
                </div>
                <ProgressBar 
                  now={uploadProgress} 
                  className="mb-3"
                  style={{ height: "12px" }}
                  striped
                  animated
                />
                <div className="text-center">
                  <Spinner size="sm" className="me-2" />
                  <small>Processando e enviando imagens...</small>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Estado vazio - mobile otimizado */}
      {!isLoading && allImages.length === 0 && (
        <Card className="text-center py-4 py-md-5">
          <Card.Body className="px-3">
            <IconPhoto size={48} className="text-muted mb-3 d-md-none" />
            <IconPhoto size={64} className="text-muted mb-4 d-none d-md-block" />
            <h4 className="h5 h-md-4">Biblioteca de imagens vazia</h4>
            <p className="text-muted mb-4 small">
              Comece carregando imagens para criar uma biblioteca rica para seus agentes IA. 
              As imagens ficarão disponíveis para uso em conversas e respostas dos agentes.
            </p>
            
            {/* Botão responsivo */}
            <div className="d-grid gap-2 d-sm-flex justify-content-sm-center">
              <Button 
                variant={accountLimits.remaining_slots === 0 ? "secondary" : "primary"}
                size="lg"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading || accountLimits.remaining_slots === 0}
                className="d-flex align-items-center justify-content-center gap-2"
                style={{ minHeight: "48px" }}
              >
                <IconUpload size={18} />
                <span>
                  {accountLimits.remaining_slots === 0 ? "Limite Atingido" : "Carregar Primeira Imagem"}
                </span>
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ImageLibrary;